import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { Stack, Button, Tooltip, Typography, IconButton } from "@financeable-com-au/financeable-ui";
import {
  applicationSelector,
  getCurrentProductLenderId,
  userDetailsSelector,
  loanDetailsSelector,
  updateLoanDetails,
  saveValue,
  setSubmissionModal,
  getApplicationDocuments,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type UserDetailsState,
  type LoanDetails,
  type SubmissionModal,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userSelector, userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument, UserState } from "@/store/slices/types/userSlice";
import { quoteCalculatorSelector } from "@/store/slices/quoteCalculator";
import { useSubmissionSteps } from "@/components/SubmissionModal/hooks/useSubmissionSteps";
import { isLodgeAISupported } from "@/utils/lenderUtils";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { buildLenderLoanDetailsPayload } from "../utils/buildLenderLoanDetailsPayload";

interface LenderDetailForSubmission extends Required<Pick<SubmissionModal, "lender" | "lodgeAI" | "productDetails">> {
  submitType: SubmissionModal["submissionType"];
}

interface LenderCardQuoteSubmitActionsProps {
  centerActions?: boolean;
}

const LenderCardQuoteSubmitActions = ({ centerActions = false }: LenderCardQuoteSubmitActionsProps) => {
  const dispatch = useDispatch();
  const { isLodgeAIEnabled, isQuotesUpliftEnabled } = useFeatureFlags();
  const { constructSubmissionSteps } = useSubmissionSteps();
  const { product, lenderCard, lenderCardCustom, isCustomQuote, inArrears } = useLenderCardContext();
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const loanDetails = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const quoteCalculator = useSelector(quoteCalculatorSelector);
  const { currentProductLenderId, isQuoteOpen: isQuote } = useSelector<ApplicationFormSlice, UserDetailsState>(
    userDetailsSelector,
  );
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const { lenderSettings } = useSelector<UserSlice, UserState>(userSelector);
  const [isSubmitting, setIsSubmitting] = useState<SubmissionModal["submissionType"] | null>();

  const isQuoteUploadEnabled = product?.actions?.includes("quote");
  const isDisableEdit = !permissions?.application?.quote_edit;
  const isConfiguredForSubmit =
    product?.lenderCredentials && product?.lenderCredentials.length > 0
      ? lenderSettings?.some((setting) => setting.lender === product?.lender)
      : true;
  const isLodgeAI = isLodgeAISupported(product);
  const isLodgeAIUpload = isLodgeAIEnabled() && isLodgeAI;
  const uploadSubmitType: SubmissionModal["submissionType"] = isLodgeAIUpload ? "browser" : "upload";

  const buildLoanDetailsPayload = () =>
    buildLenderLoanDetailsPayload({
      loanDetails,
      quoteCalculator,
      product,
      lenderCard,
      lenderCardCustom,
      isCustomQuote,
      inArrears,
      applicationType: application.applicationType,
    });

  const handle = {
    applicationSubmit: async ({ lender, submitType, productDetails, lodgeAI }: LenderDetailForSubmission) => {
      setIsSubmitting(submitType);
      await dispatch(updateLoanDetails(buildLoanDetailsPayload())).unwrap();
      dispatch(saveValue({ applyApplication: null }));
      dispatch(getCurrentProductLenderId(lender));
      dispatch(getApplicationDocuments({ applicationId: application._id }));
      const submissionSteps = constructSubmissionSteps({ lenderSubmissionStep: product?.submissionSteps ?? [] });
      const submissionModal: SubmissionModal = {
        steps: submissionSteps,
        isOpen: true,
        submissionType: submitType,
        lender,
        lodgeAI,
        productDetails,
        submission: {
          status: undefined,
          message: undefined,
        },
      };
      dispatch(setSubmissionModal(submissionModal));
      setIsSubmitting(null);
    },
    submitQuote: () => {
      const lenderDetail: LenderDetailForSubmission = {
        submitType: uploadSubmitType,
        lender: product?.lender ?? "",
        lodgeAI: product?.lodgeAI ?? { cloud: false, native: false },
        productDetails: {
          productName: product?.productName ?? "",
          productType: product?.productType ?? "",
        },
      };
      handle.applicationSubmit(lenderDetail);
    },
  };

  // Only show Submit Quote for quote-upload products on the uplifted quote page.
  if (!product || !isQuoteUploadEnabled || !isQuote || !isQuotesUpliftEnabled()) {
    return null;
  }

  return (
    <Stack
      flexDirection="row"
      flexWrap="wrap"
      columnGap={3}
      rowGap={1}
      alignItems="center"
      justifyContent={centerActions ? "center" : "flex-start"}
      width={centerActions ? "100%" : undefined}
    >
      <div>
        <Button
          variant="primary"
          intent="neutral"
          size="sm"
          disabled={!isConfiguredForSubmit || isDisableEdit}
          endDecorator={<RequestQuoteOutlinedIcon sx={{ fontSize: "1rem" }} />}
          onClick={handle.submitQuote}
          loading={isSubmitting === uploadSubmitType && currentProductLenderId === product?.lender}
        >
          Submit Quote
        </Button>
        {!isConfiguredForSubmit && (
          <Tooltip
            title={
              <Stack padding={2} flexDirection="column" rowGap={2}>
                <Typography level="body-sm" bold>
                  {`Looks like you're not setup with ${product?.lender}.`}
                </Typography>
                <Typography mb={2}>Click on the link below to add your credentials.</Typography>
                <Link to="/settings/lenders">
                  <Button variant="secondary" size="sm">
                    Add Credentials
                  </Button>
                </Link>
              </Stack>
            }
          >
            <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
              <InfoIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Stack>
  );
};

export { LenderCardQuoteSubmitActions };
